import { useState } from "react";
import { AlertTriangle, CheckCircle2, Plus, Receipt, Wallet } from "lucide-react";
import { MasterShell } from "@/shared/layouts/MasterShell.jsx";
import { Card } from "@/shared/ui/card.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Input } from "@/shared/ui/input.jsx";
import { Label } from "@/shared/ui/label.jsx";
import { Spinner, EmptyState } from "@/shared/ui/spinner.jsx";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/shared/ui/table.jsx";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/shared/ui/dialog.jsx";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/ui/select.jsx";
import { formatCurrency, formatDate } from "@/shared/lib/format.js";
import { useAuth } from "@/features/auth/auth-context.jsx";
import { canWriteMaster } from "@/shared/lib/roles.js";
import { StatCard, InvoiceStatusBadge, INVOICE_STATUS_OPTIONS } from "./components/MasterUi.jsx";
import {
  useCreateInvoice, useMasterClients, useMasterInvoices, useMasterPlans,
  useMasterStats, useUpdateInvoice,
} from "./use-master.js";

/** Cobranças e recebimentos. */
export function InvoicesPage() {
  const { user } = useAuth();
  const podeEscrever = canWriteMaster(user);

  const [status, setStatus] = useState("todos");
  const { data: faturas = [], isLoading } = useMasterInvoices({ status });
  const { data: stats } = useMasterStats();

  const atualizar = useUpdateInvoice();
  const [novaAberta, setNovaAberta] = useState(false);

  /** Uma cobrança pendente cuja data já passou está, na prática, atrasada. */
  const hoje = new Date().toISOString().slice(0, 10);

  return (
    <MasterShell
      title="Cobranças"
      subtitle="Faturas emitidas e situação de recebimento"
      actions={
        podeEscrever && (
          <Button size="sm" className="gap-1.5" onClick={() => setNovaAberta(true)}>
            <Plus className="h-4 w-4" />
            Nova cobrança
          </Button>
        )
      }
    >
      <div className="space-y-4 p-6">
        {stats && (
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard label="Recebido" value={formatCurrency(stats.invoices.recebido)} icon={CheckCircle2} accent="success" />
            <StatCard label="A receber" value={formatCurrency(stats.invoices.aReceber)} icon={Wallet} accent="warning" />
            <StatCard
              label="Em atraso"
              value={formatCurrency(stats.invoices.atrasado)}
              hint={`${stats.invoices.qtdAtrasadas} fatura(s)`}
              icon={AlertTriangle}
              accent="destructive"
            />
          </div>
        )}

        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Situação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas as situações</SelectItem>
                {INVOICE_STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-t-0 hover:bg-transparent">
                <TableHead>Fatura</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead>Valor</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead>Forma</TableHead>
                <TableHead className="w-32" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center">
                    <Spinner className="mx-auto h-6 w-6 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : faturas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <EmptyState icon={Receipt} title="Nenhuma cobrança encontrada" />
                  </TableCell>
                </TableRow>
              ) : (
                faturas.map((fatura) => {
                  const vencida = fatura.status === "pendente" && fatura.dueDate < hoje;
                  return (
                    <TableRow key={fatura.id}>
                      <TableCell className="font-mono text-xs">{fatura.id}</TableCell>
                      <TableCell className="text-sm">{fatura.clinicName}</TableCell>
                      <TableCell className="text-sm">{fatura.planName}</TableCell>
                      <TableCell className="text-sm font-medium">
                        {formatCurrency(fatura.amount)}
                      </TableCell>
                      <TableCell className="text-xs">
                        <span className={vencida ? "font-medium text-destructive" : ""}>
                          {formatDate(fatura.dueDate)}
                        </span>
                        {vencida && (
                          <span className="ml-1 text-[10px] text-destructive">(vencida)</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <InvoiceStatusBadge status={fatura.status} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {fatura.method || "—"}
                      </TableCell>
                      <TableCell>
                        {podeEscrever && fatura.status !== "paga" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5"
                            disabled={atualizar.isPending}
                            onClick={() => atualizar.mutate({ id: fatura.id, status: "paga" })}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Marcar paga
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      <NewInvoiceDialog aberto={novaAberta} onClose={() => setNovaAberta(false)} />
    </MasterShell>
  );
}

function NewInvoiceDialog({ aberto, onClose }) {
  const criar = useCreateInvoice();
  const { data: clientes = [] } = useMasterClients();
  const { data: planos = [] } = useMasterPlans();

  const [form, setForm] = useState({
    clinicId: "",
    planId: "",
    amount: "",
    dueDate: "",
    method: "Boleto",
  });

  const set = (campo) => (valor) => setForm((atual) => ({ ...atual, [campo]: valor }));

  /** Escolher o cliente já sugere o plano e o valor do contrato dele. */
  function escolherCliente(clinicId) {
    const cliente = clientes.find((c) => c.id === clinicId);
    setForm((atual) => ({
      ...atual,
      clinicId,
      planId: cliente?.planId ?? atual.planId,
      amount: cliente ? String(cliente.monthlyValue) : atual.amount,
    }));
  }

  function submeter(evento) {
    evento.preventDefault();
    criar.mutate(
      { ...form, amount: Number(form.amount) || 0, planId: form.planId || null },
      {
        onSuccess: () => {
          setForm({ clinicId: "", planId: "", amount: "", dueDate: "", method: "Boleto" });
          onClose();
        },
      },
    );
  }

  return (
    <Dialog open={aberto} onOpenChange={(estado) => !estado && onClose()}>
      <DialogContent>
        <form onSubmit={submeter}>
          <DialogHeader>
            <DialogTitle>Nova cobrança</DialogTitle>
            <DialogDescription>Gere uma fatura avulsa para um cliente.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label>Cliente</Label>
              <Select value={form.clinicId} onValueChange={escolherCliente}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.tradeName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Plano</Label>
              <Select value={form.planId} onValueChange={set("planId")}>
                <SelectTrigger>
                  <SelectValue placeholder="Opcional" />
                </SelectTrigger>
                <SelectContent>
                  {planos.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoice-amount">Valor (R$)</Label>
              <Input
                id="invoice-amount"
                type="number"
                min="0"
                step="0.01"
                required
                value={form.amount}
                onChange={(e) => set("amount")(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoice-due">Vencimento</Label>
              <Input
                id="invoice-due"
                type="date"
                required
                value={form.dueDate}
                onChange={(e) => set("dueDate")(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoice-method">Forma de pagamento</Label>
              <Input
                id="invoice-method"
                value={form.method}
                onChange={(e) => set("method")(e.target.value)}
                placeholder="Boleto, Pix, Cartão..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={criar.isPending || !form.clinicId}>
              {criar.isPending ? <Spinner /> : null}
              Gerar cobrança
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
